<template>
  <div 
    ref="containerRef"
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada || showQRCode }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada || showQRCode }"
      ref="formCardRef"
    >
      <!-- Vista de QR confirmado -->
      <div
        v-if="asistenciaConfirmada || showQRCode"
        class="qr-view text-center"
        ref="qrViewRef"
      >
        <p class="qr-header">Digital Pass</p>
        <div class="qr-wrapper">
          <img v-if="qrCode" :src="qrCode" alt="QR Code" class="qr-code">
          <p v-else class="qr-error-message">QR code has not been generated</p>
        </div>
        <p class="qr-instructions">Present it on the day of the event</p>
      </div>

      <!-- Formulario paso a paso -->
      <div v-else>
        <div class="form-header">
          <h2 ref="tituloAnimado" class="form-title">
            Confirm <span class="subtitulo">Attendance</span>
            
          </h2>
          <p class="fecha-limite">Please confirm attendance <br>   by April 21</p>        </div>

          
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
            ></div>
          </div>
<p class="form-subtitle">Step {{ currentStep }} of {{ totalSteps }}</p>

        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <!-- Paso 1: Nombre y Teléfono -->
          <div v-if="currentStep === 1" class="step-box">
            <div class="form-group">
              <label for="name" class="form-label">Name</label>
              <input
                id="name"
                v-model.trim="name"
                type="text"
                class="form-control"
                placeholder="Enter your full name"
                required
              >
            </div>
            <div class="form-group">
              <label for="phone" class="form-label">Phone</label>
              <input
                id="phone"
                v-model.trim="phone"
                type="tel"
                class="form-control"
                placeholder="Enter your phone number"
                required
                pattern="[0-9]{10}"
                title="Enter a 10-digit number"
              >
            </div>
          </div>

          <!-- Paso 2: Invitado por (oculto en XV o YM) -->
          <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
            <div class="form-group">
              <label class="form-label">Invited by?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="novia" v-model="invitedBy" type="radio" value="novia" class="radio-input" required>
                  <label for="novia" class="radio-label">Bride</label>
                </div>
                <div class="radio-option">
                  <input id="novio" v-model="invitedBy" type="radio" value="novio" class="radio-input">
                  <label for="novio" class="radio-label">Groom</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 3: Asistencia -->
          <div v-if="currentStep === asistenciaStep" class="step-box">
            <div class="form-group">
              <label class="form-label">Will you attend?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="si" v-model="attending" type="radio" value="si" class="radio-input" required>
                  <label for="si" class="radio-label">Yes</label>
                </div>
                <div class="radio-option">
                  <input id="no" v-model="attending" type="radio" value="no" class="radio-input">
                  <label for="no" class="radio-label">No</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 4: Número de invitados -->
          <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
            <div class="form-group">
              <label for="guests" class="form-label">How many will attend?</label>
              <select
                id="guests"
                v-model.number="guests"
                class="form-select"
                :disabled="attending === 'no' || loading"
                required
              >
                <option :value="null" disabled>Select an option</option>
                <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
              </select>
              <div v-if="loading" class="loading-indicator">Loading options...</div>
            </div>
          </div>

          <!-- Botones de navegación -->
          <div class="d-flex justify-content-between mt-3 buttons-row">
            <button type="button" class="submit-button secondary" @click="prevStep" v-if="currentStep > 1">
              Back
            </button>
            <button
              v-if="currentStep < totalSteps"
              type="button"
              class="submit-button"
              @click="nextStep"
            >
              Next
            </button>
            <button
              v-if="currentStep === totalSteps"
              type="submit"
              class="submit-button"
              :disabled="isSubmitting"
            >
              <span v-if="!isSubmitting">Confirm</span>
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </button>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
        </form>
      </div>
    </div>

    <div v-show="showTooltip" ref="tooltipRef" class="tooltip-reminder">
    </div>
  </div>
</template>
<script>
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import axios from 'axios';
import baseUrl from '@/api';
import gsap from 'gsap';
import Swal from 'sweetalert2';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: { type: Number, required: true, validator: v => Number.isInteger(v) && v > 0 },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {
    const name = ref('');
    const phone = ref('');
    const invitedBy = ref('');
    const attending = ref('');
    const guests = ref('');
    const guestsOptions = ref([1, 2]);
    const loading = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');
    
    const tituloAnimado = ref(null);

    const qrCode = ref(null);
    const showQRCode = ref(false);
    const asistenciaConfirmada = ref(false);
    const currentStep = ref(1);
    const formCardRef = ref(null);

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase();
      return ['xv', 'ym'].includes(eventName);
    });

    const totalSteps = computed(() => isXvEvent.value ? 3 : 4);
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3);

  const animateStep = () => {
  nextTick(() => {
    gsap.fromTo(formCardRef.value.querySelector('.step-box'),
      { y: 50, opacity: 0 }, // Comienza 50px más abajo e invisible
      { 
        y: 0,                // Termina en su posición original
        opacity: 1, 
        duration: 0.6, 
        ease: 'power2.out',
        // Opcional: puedes añadir perspectiva para efecto 3D
        transformPerspective: 300,
        rotationX: -10       // Ligera inclinación para efecto de hoja
      });
  });
};
const nextStep = async () => {
  // Validación paso 1: Nombre y teléfono
if (currentStep.value === 1) {
  if (!name.value.trim() || !phone.value.trim()) {
    errorMessage.value = 'Please enter your name and phone only numbers .';
    return;
  
    }
  }

  // Validación paso 2 (si aplica): Invitado por
  if (currentStep.value === 2 && !isXvEvent.value) {
    if (!invitedBy.value) {
errorMessage.value = 'Please select who invited you.';
      return;
    }
  }

  // Validación paso 3: Asistencia
  if (currentStep.value === asistenciaStep.value) {
    if (!attending.value) {
errorMessage.value = 'Please select whether you will attend or not.';
      return;
    }

     if (attending.value === 'no') {
          currentStep.value = totalSteps.value;
          await nextTick();
          handleSubmit();
          return;
        }
      }

      // Último paso (si asiste): cuántos asisten
      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (guests.value == null || Number.isNaN(Number(guests.value))) {
          errorMessage.value = 'Por favor selecciona cuántas personas asistirán.';
          return;
        }
      }

      // OK -> avanzar
      errorMessage.value = '';
      if (currentStep.value < totalSteps.value) {
        currentStep.value++;
        animateStep();
      }
    };



    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
        animateStep();
      }
    };

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(`${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`);
        const numPases = response.data.num_pase;
        guestsOptions.value = numPases > 0 ? Array.from({ length: numPases }, (_, i) => i + 1) : [1, 2];
      } catch (error) {
        console.error('Error al obtener límite de invitados:', error);
errorMessage.value = 'Error loading guest options.';
      } finally {
        loading.value = false;
      }
    };

    const verificarAsistencia = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/verificar-asistencia`, {
          idInvitado: props.idInvitado
        });
        if (response.data.asistio) {
          asistenciaConfirmada.value = true;
          await obtenerCodigoQR();
        }
      } catch (error) {
        console.error('Error al verificar asistencia:', error);
      }
    };

    const obtenerCodigoQR = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/codigoqrinvitado`, { id_invitado: Number(props.idInvitado) });
        qrCode.value = response.data?.codigoqr || null;
      } catch (error) {
        console.error('Error obteniendo código QR:', error);
        errorMessage.value = 'Error al generar el código QR';
      }
    };
const handleSubmit = async () => {
  if (attending.value === 'no') guests.value = '0';

  isSubmitting.value = true;
  errorMessage.value = '';

  try {
    const formData = {
      nombre_asistencia: name.value.trim(),
      telefono_invitado: phone.value.trim(),
      invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
      asistira: attending.value,
      id_festejada: props.idFestejado,
      id_invitado: props.idInvitado,
  num_pases: Number(guests.value), // ← CORREGIDO AQUÍ
    };

    await axios.post(`${baseUrl}/asistencia`, formData);

    if (attending.value === 'no') {
      Swal.fire({
        icon: 'info',
        title: '¡Gracias por avisarnos!',
        text: 'Sentimos mucho que no puedas asistir.',
        confirmButtonColor: '#d88cbd',
      });
      asistenciaConfirmada.value = true; // Para ocultar el formulario
    } else {
      await obtenerCodigoQR();
      showQRCode.value = true;
    }
  } catch (error) {
    console.error('Error confirmando asistencia:', error);
    errorMessage.value = 'Hubo un error al confirmar. Por favor intenta nuevamente.';
  } finally {
    isSubmitting.value = false;
  }
};

    watch(attending, (newValue) => {
      if (newValue === 'no') {
        guests.value = '0';
      } else if (newValue === 'si' && guests.value === '0') {
        guests.value = '1';
      }
    });

    onMounted(() => {
      fetchGuestLimit();
      verificarAsistencia();
      animateStep();
    gsap.fromTo(tituloAnimado.value,
  { y: -150, opacity: 0, scale: 1.2 },
  {
    y: 0,
    opacity: 1,
    scale: 1,
    duration: 1.2,
    ease: 'bounce.out',
    scrollTrigger: {
      trigger: tituloAnimado.value,
      start: 'top 80%',
toggleActions: 'restart none none reset'
    }
  }
);

    });

    return {
      name, phone, invitedBy, attending, guests, guestsOptions,
      loading, isSubmitting, errorMessage, isXvEvent, asistenciaConfirmada,
      qrCode, showQRCode, currentStep, nextStep, prevStep, handleSubmit,
      totalSteps, asistenciaStep, formCardRef,  tituloAnimado // <-- ¡FALTABA ESTO!

    };
  }
};
</script>
<style scoped>
.confirmation-container {
  min-height: 70vh;
  padding: 2rem 1rem;
  background: radial-gradient(circle at top, rgba(143,187,223,0.12), transparent 55%);
  transition: all 0.3s ease;
  position: relative;
}

.confirmation-card {
  width: 100%;
  max-width: 500px;
  background: linear-gradient(to bottom, #ffffff, #f9f9f9);
  border: 1px solid #e0e0e0;
  border-radius: 1.5rem;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.confirmation-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.16);
}

/* Vista compacta para QR */
.compact-view {
  min-height: 70vh;
  align-items: flex-start;
  padding-top: 4rem;
}

.compact-card {
  max-width: 400px;
  padding: 2rem;
}

/* Encabezado del formulario */
.form-header {
  margin-bottom: 2rem;
  text-align: center;
}

.form-title {
  font-family: 'titulos', cursive;
  color:#043b73;
  font-size: 53px !important;
  color:#043b73;
  margin-bottom: 0.5rem;
}

.form-title .subtitulo {
  font-size: 53px !important;
}

.form-subtitle {
  font-family: 'Karla', sans-serif;
  color: #043b73;
  font-size: 0.95rem;
}

/* Grupos de formulario */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'Karla', sans-serif;
  font-weight: 500;
  color: #495057;
}

/* Controles de formulario */
.form-control,
.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.75rem;
  background-color: rgba(255, 255, 255, 0.9);
  transition: border-color 0.3s, box-shadow 0.3s, transform 0.1s;
}

.form-control:focus,
.form-select:focus {
  border-color: #043b73;
  box-shadow: 0 0 0 0.15rem rgba(143, 187, 223, 0.35);
  outline: none;
  transform: translateY(-1px);
}

/* Radio buttons */
.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-option {
  display: flex;
  align-items: center;
}

.radio-input {
  margin-right: 0.5rem;
  accent-color: #102553;
}

.radio-label {
  font-family: 'Karla', sans-serif;
  color: #495057;
}

/* Botones */
.buttons-row {
  gap: 0.75rem;
}

.submit-button {
  flex: 1;
  padding: 0.75rem;
  font-family: 'FeelingPassionate', cursive;
  font-size: 1.25rem;
  color: #f9f9f9;
  background: rgb(36, 74, 156);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 8px 18px rgba(143, 187, 223, 0.45);
}

.submit-button.secondary {
  background-color: #f3f5f9;
  color: #4b6070;
  box-shadow: none;
}

.submit-button:hover:not(:disabled) {
  background-color: #7daed3;
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(143, 187, 223, 0.6);
}

.submit-button.secondary:hover {
  background-color: #e1e7f0;
}

.submit-button:disabled {
  background: rgb(36, 74, 156);
  opacity: 0.7;
  cursor: not-allowed;
}

/* Vista QR */
.qr-view {
  padding: 1rem;
}.fecha-limite {
  font-family: 'padres', sans-serif;
  font-size: 1.2rem;
  color: #0a0a0a; /* Un tono suave que no compite con el título */
  margin-top: 8px;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: inline-block;
  padding-top: 8px;
}

.qr-header {
  font-family: 'Westony', cursive;
  font-size: 1.75rem;
  color: #043b73;
  margin-bottom: 1.5rem;
}

.confirmation-thanks {
  font-family: 'Karla', sans-serif;
  font-weight: 600;
  color: #043b73;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.qr-wrapper {
  margin: 1rem 0;
}

.qr-code {
  width: 190px;
  height: 190px;
  border: 8px solid white;
  border-radius: 1rem;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.16);
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 0.95rem;
  margin-top: 1rem;
}

/* Mensajes de error */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}

.qr-error-message {
  color: #dc3545;
  font-weight: 500;
}

.loading-indicator {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

/* Tooltip recordatorio */
.tooltip-reminder {
  position: fixed;
  bottom: 18px;
  right: 18px;
  padding: 0.6rem 1.2rem;
  font-family: 'Karla', sans-serif;
  font-size: 0.9rem;
  color: #4b6070;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

/* Progress bar */
.progress-container {
  height: 8px;
  margin-top: 0px;
  background-color: #e0e0e0;
  border-radius: 999px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #8fbbdf, #d592b0);
  transition: width 0.4s ease;
}

/* Responsive */
@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
  }
  
  .form-title {
    font-size: 1.9rem;
  }
  
  .radio-group {
    gap: 1rem;
  }

  .tooltip-reminder {
    bottom: 12px;
    right: 12px;
    font-size: 0.8rem;
  }
}
</style>
